import { Box, Button, Group, Modal, PasswordInput, SegmentedControl, Switch, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import HelpPopover from '../HelpPopover/HelpPopover';
import SettingRow from '../SettingRow/SettingRow';
import { HELP, applyExample } from './aiModelsHelp';

const API_STYLES = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
];
const BASE_URL_HINT = {
  openai: 'Include /v1. Blank uses the vendor API.',
  anthropic: 'Root only, without /v1. Blank uses the vendor API.',
};
const LOCALHOST = /^https?:\/\/(localhost|127\.)/i;

const LlmProviderModal = ({ provider: passedProvider, onClose, onSave }) => {
  const fetch = useFetch();
  const [provider, setProvider] = useState(passedProvider);
  // The stored key is never sent to the browser: apiKey is only what is typed here, and null clears it.
  const [apiKey, setApiKey] = useState('');
  const [replacingKey, setReplacingKey] = useState(false);
  const [saveError, setSaveError] = useState();

  useEffect(() => {
    setProvider(passedProvider);
    setApiKey('');
    setReplacingKey(false);
    setSaveError(undefined);
  }, [passedProvider]);

  if (provider === undefined || !passedProvider) {
    return '';
  }

  const set = (field) => (value) => setProvider((current) => ({ ...current, [field]: value }));
  const label = provider.label || '';
  const key = provider.key || '';
  const apiStyle = provider.apiStyle || 'openai';
  const baseUrl = provider.baseUrl || '';
  const baseUrlChanged = !!provider.id && baseUrl.trim() !== (passedProvider.baseUrl || '');
  const showKeyInput = !provider.hasApiKey || replacingKey || baseUrlChanged;

  const save = (event) => {
    event.preventDefault();
    setSaveError(undefined);

    const payload = {
      llmProvider: {
        label: label.trim(),
        // Seeded rows run on their legacy branches, which ignore both; sending them would reset their models' tests.
        ...(!provider.seeded && { apiStyle, baseUrl: baseUrl.trim() || null }),
        enabled: provider.enabled ?? true,
        ...(!provider.id && { key: key.trim() }),
        ...(apiKey === null || apiKey.trim() ? { apiKey: apiKey === null ? null : apiKey.trim() } : {}),
      },
    };
    const request = provider.id
      ? fetch(`/api/admin/llmProviders/${provider.id}`, { method: 'PUT', body: JSON.stringify(payload) })
      : fetch('/api/admin/llmProviders', { method: 'POST', body: JSON.stringify(payload) });

    request
      .then(() => {
        onSave();
        onClose();
      })
      .catch(setSaveError);
  };

  const keyField = () => {
    if (provider.seeded) {
      return (
        <Text size="sm" c="dimmed">
          Releva token (env)
        </Text>
      );
    }
    if (apiKey === null) {
      return (
        <Group gap="xs">
          <Text size="sm">The key is cleared on save.</Text>
          <Button size="compact-xs" variant="subtle" onClick={() => setApiKey('')}>
            Undo
          </Button>
        </Group>
      );
    }
    return (
      <>
        {provider.hasApiKey && (
          <Group gap="xs" mb={showKeyInput ? 'xs' : 0}>
            <Text size="sm">Key set **** {provider.apiKeyLast4}</Text>
            {!showKeyInput && (
              <Button size="compact-xs" variant="subtle" onClick={() => setReplacingKey(true)}>
                Replace
              </Button>
            )}
            <Button size="compact-xs" variant="subtle" color="red" onClick={() => setApiKey(null)}>
              Clear
            </Button>
          </Group>
        )}
        {showKeyInput && (
          <PasswordInput
            id="apiKey"
            size="xs"
            autoComplete="new-password"
            placeholder="Blank for a host without a key"
            description={baseUrlChanged && provider.hasApiKey ? 'Enter the key again for the new base URL.' : undefined}
            value={apiKey}
            onChange={(event) => setApiKey(event.currentTarget.value)}
          />
        )}
      </>
    );
  };

  return (
    <Modal opened={!!passedProvider} onClose={onClose} size="lg" title={`${provider.id ? 'Edit' : 'Add'} provider`}>
      <Box component="form" ta="left" onSubmit={save}>
        <SettingRow label="Label*" htmlFor="label">
          <TextInput id="label" size="xs" required value={label} onChange={(e) => set('label')(e.target.value)} />
        </SettingRow>
        <SettingRow
          label="Key*"
          htmlFor="key"
          help={!provider.id && <HelpPopover {...HELP.key} onUse={applyExample(set('key'), key)} />}
        >
          <TextInput
            id="key"
            size="xs"
            required
            disabled={!!provider.id}
            value={key}
            onChange={(e) => set('key')(e.target.value)}
          />
        </SettingRow>
        <SettingRow label="API style" help={<HelpPopover {...HELP.apiStyle[apiStyle]} />}>
          <SegmentedControl
            size="xs"
            data={API_STYLES}
            value={apiStyle}
            disabled={!!provider.seeded}
            onChange={set('apiStyle')}
          />
        </SettingRow>
        <SettingRow
          label="Base URL"
          htmlFor="baseUrl"
          help={!provider.seeded && <HelpPopover {...HELP.baseUrl} onUse={applyExample(set('baseUrl'), baseUrl)} />}
        >
          <TextInput
            id="baseUrl"
            size="xs"
            placeholder="(vendor default)"
            disabled={!!provider.seeded}
            description={BASE_URL_HINT[apiStyle]}
            error={
              LOCALHOST.test(baseUrl.trim()) ? 'localhost is the API pod itself; use a cluster name or IP.' : undefined
            }
            value={baseUrl}
            onChange={(e) => set('baseUrl')(e.target.value)}
          />
        </SettingRow>
        <SettingRow label="API key" htmlFor="apiKey" help={<HelpPopover {...HELP.apiKey} />}>
          {keyField()}
        </SettingRow>
        <SettingRow label="Enabled" htmlFor="enabled">
          <Switch
            id="enabled"
            size="xs"
            checked={provider.enabled ?? true}
            onChange={(e) => set('enabled')(e.currentTarget.checked)}
          />
        </SettingRow>
        {saveError && <ErrorMessage message={saveError.message} />}
        <Group justify="flex-end" gap="xs" py="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={label.trim() === '' || key.trim() === ''}>
            <IconDeviceFloppy size={14} /> Save
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export default LlmProviderModal;
